import { useFeatureGate } from '@statsig/expo-bindings';
import { createContext, useContext, useEffect, useState } from 'react';

import { useUserAuthorization } from '../../auth/hooks/useUserAuthorization';
import { LimitedAccessScope } from '../../auth/types/UserAuthorization';
import { FeatureFlags } from '../../feature-flags/FeatureFlags';
import { StorageKeys } from '../constants';
import type { ContainerComponent } from '../types/Common';
import { useStorage } from './StorageProvider';

export enum BankingExperience {
    LEGACY = 'legacy',
    CASH_CARD_ACCT = 'cashCardAcct',
}

const BankingExperienceContext = createContext({
    enableBankingExperienceToggle: false,
    bankingExperience: BankingExperience.LEGACY,
    setBankingExperience: (_: BankingExperience) => {},
});

export const useBankingExperience = () => {
    return useContext(BankingExperienceContext);
};

export const BankingExperienceProvider: ContainerComponent = ({ children }) => {
    const storage = useStorage();

    const { limitedAccessScope } = useUserAuthorization();

    const limitedAccessHeaderShown =
        limitedAccessScope === LimitedAccessScope.BANKING ||
        limitedAccessScope === LimitedAccessScope.KYC;

    const [bankingExperience, setBankingExperienceLocalState] = useState(
        BankingExperience.LEGACY,
    );

    const setBankingExperience = (bankingExperience: BankingExperience) => {
        setBankingExperienceLocalState(bankingExperience);
        storage.setString(
            StorageKeys.BANKING_EXPERIENCE_OVERRIDE,
            bankingExperience,
        );
    };

    const { value: enableBankingExperienceToggle } = useFeatureGate(
        FeatureFlags.ENABLE_BANKING_EXPERIENCE_TOGGLE,
    );

    const override = enableBankingExperienceToggle
        ? (storage.getString(
              StorageKeys.BANKING_EXPERIENCE_OVERRIDE,
          ) as BankingExperience)
        : undefined;

    const value = override ?? BankingExperience.CASH_CARD_ACCT;

    useEffect(() => {
        setBankingExperienceLocalState(value);
    }, [value]);

    if (limitedAccessHeaderShown) {
        return (
            <BankingExperienceContext.Provider
                value={{
                    enableBankingExperienceToggle: false,
                    bankingExperience: BankingExperience.LEGACY,
                    setBankingExperience,
                }}
            >
                {children}
            </BankingExperienceContext.Provider>
        );
    } else {
        return (
            <BankingExperienceContext.Provider
                value={{
                    enableBankingExperienceToggle,
                    bankingExperience,
                    setBankingExperience,
                }}
            >
                {children}
            </BankingExperienceContext.Provider>
        );
    }
};
